import {
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
} from "@clerk/clerk-react";
import { Routes, Route, useNavigate, Navigate, useParams } from "react-router-dom";
import {
  Acquisitions,
  Candidacies,
  CandidacyDetail,
  Buildings,
  Settings,
  Tasks,
  Vennootschappen,
  MergerProgressPage,
  BugReportsPage,
  BugReportDetailPage,
} from "./screens";
import NotesOverviewSimple from "./screens/notes/notes-overview-simple";
import { EpicDetailPage } from "./screens/bug-reports/epics/epic-detail";
import { PricingSimulatorPage } from "./screens/pricing-simulator";
import { SharedCalendarPage } from "./screens/sharedCalendar/SharedCalendarPage";
import AssetCategoryManagementScreen from "./screens/assetCategoryManagement";
import { AssetDetailPage } from "./screens/assets/AssetDetailPage";
import AssetBulkCreatorPage from "./screens/assets/AssetBulkCreatorPage";
// AssetCreatorPage deprecated — moved to screens/assets/deprecated/
import AssetsScreen from "./screens/assets";
import AccessControlPage from "./screens/accesscontrol/accesscontrol";
import { ReportAssetIssuePage } from "./screens/public/ReportAssetIssuePage";
import { ReportMaintenancePage } from "./screens/public/ReportMaintenancePage";
import DocumentGenerator from "./screens/document-generator/DocumentGenerator";
import EntitySchemaVisualization from "./screens/entity-schema/EntitySchemaVisualization";
import { NavMenu } from "shared/components";
import { RoutesWrapper } from "components/index.ts";
import { TasksProvider } from "shared/contexts/tasks.context";
import { UsersProvider } from "shared/contexts/users.context";
import { PermissionsProvider } from "shared/contexts/permissions.context";
import { SettingsProvider } from "shared/contexts/settings.context";
import { EntityFieldProvider } from "shared/contexts/EntityFieldContext";
import { KeyboardShortcutsProvider } from "shared/contexts/keyboardShortcuts/keyboardShortcuts.context";
import {
  RequireBuildingsAccess,
  PermissionGuard,
  RequireContactsAccess,
  RequireAcquisitionsAccess,
  RequireSettingsAccess,
  RequireTasksAccess,
  RequireNotesAccess,
  RequireHelpdeskAccess,
  RequireDueDiligenceAccess,
  RequireRenovationAccess,
  RequireMaintenanceAccess,
  RequireDocumentsAccess,
  RequirePicturesAccess,
  RequireGeneralAccess,
  RequireAnyPermission,
  RequireRentalAccess,
  RequireInsuranceAccess,
  RequireConversationsAccess,
  RequireClustersAccess,
  RequireBuildingAttributesAccess,
  RequireCorporateAccess,
} from "components/auth/PermissionGuard";
import Contacts from "screens/contacts/contacts.tsx";
import GebouwContacten from "screens/contacts/gebouwContacten.tsx";
import ContactDetails from "screens/contactDetails/contactDetails.tsx";
import CompanyDetails from "screens/companyDetails/companyDetails.tsx";
import { ResourcesProvider } from "shared/contexts/resources.context";
import { ScrollToTop } from "shared/utils/scroll-to-top";
import MenuProvider from "shared/components/menuProvider/menuProvider.tsx";
import Building from "screens/buildings/building/building";
import ContractCreatePage from "screens/buildings/components/rentalTab/ContractCreatePage";
import { useLocation } from "react-router-dom";
import { UnitInformation } from "screens/buildings/units/unit/unit-information";
import { TooltipProvider } from "components/ui/tooltip";
import { HelpdeskListPage, HelpdeskKanbanPage } from "screens/helpdesk/tickets";
import { HelpdeskStatisticsPage } from "screens/helpdesk/statistics";
import { OpexInvoicesPage } from "screens/helpdesk/opex-invoices";
import ImagesGallery from "shared/components/imagesGallery/imagesGallery";
import FilesOverview from "shared/components/filesOverview/filesOverview";
import ZipDownloadPage from "screens/downloads/ZipDownloadPage";
import NotificationsDashboard from "screens/notifications/NotificationsDashboard";
import Rental from "shared/components/rental/rental";
import Renovation from "shared/components/renovation/renovation";
import { IndexatiesPage } from "screens/rental/indexaties";
import { IndexationCalculationPage } from "screens/rental/indexaties/IndexationCalculationPage";

// Rental pages
import RentalStatistics from "screens/rental/pages/statistics";
import Milestones from "screens/rental/pages/milestones";
import Vacancies from "screens/rental/pages/vacancies";
import Incasso from "screens/rental/pages/incasso";
import { IncassoKanbanPage } from "screens/rental/pages/incasso/pages/IncassoKanbanPage";
import { IncassoListPage } from "screens/rental/pages/incasso/pages/IncassoListPage";
import { IncassoDossierDetailPage } from "screens/rental/pages/incasso/pages/IncassoDossierDetailPage";
import Watchlist from "screens/rental/pages/watchlist";
import MissingDocuments from "screens/rental/pages/missing-documents";
import AllContractsPage from "screens/rental/pages/all-contracts";
import ValueCreation from "screens/rental/pages/value-creation";
import ContractsThisYearPage from "screens/rental/pages/contracts-this-year";
import OutgoingTenants from "screens/rental/pages/outgoing-tenants";
import IncomingTenants from "screens/rental/pages/incoming-tenants/incoming-tenants";
import HerverhuurOverzicht from "screens/rental/pages/herverhuur";
import AcquisitionDetails from "screens/acquisitions/acquisition/acquisition";
import AcquisitionStatisticsPage from "screens/acquisitions/statistics/AcquisitionStatisticsPage";
import DueDiligence from "screens/duediligence/duediligence";
import ChatPopup from "shared/components/chat/chat";
import InsuranceFiles from "screens/buildings/components/insurances/insuranceOverview";
import InsuranceDetail from "screens/buildings/components/insurances/insuranceDetail";
import { HelpdeskTicketDetailPage } from "screens/helpdesk/helpdeskTicketDetail/HelpdeskTicketDetailPage";
import { QuickAddTicketPage } from "screens/helpdesk/quickAddTicketPage";
import TwilioConversations from "components/customer-chat/twilioConversations";
// Keep old page for project CREATION (has special budget selection logic)
// NEW: Using UniversalDetailPage-based component for VIEWING/EDITING existing projects
import { RenovationProjectDetailPage } from "screens/renovationProject/RenovationProjectDetailPage";
// Project Part Detail - dedicated page for part details with navigation context
import { ProjectPartDetailPage } from "screens/renovationProject/ProjectPartDetailPage";
import RenoHelpdeskListPage from "screens/renoHelpdesk/RenoHelpdeskListPage";
import RenoHelpdeskTicketDetailPage from "screens/renoHelpdesk/RenoHelpdeskTicketDetailPage";
import { RenovationDocumentsList } from "screens/renovationDocuments/RenovationDocumentsList";
import { RenovationProjectsListPage } from "screens/renovationProjects/RenovationProjectsListPage";
import { InvestmentItemsListPage } from "screens/investment/InvestmentItemsListPage";
import { InvestmentItemDetailRoute } from "screens/investment/InvestmentItemDetailRoute";
import {
  MaintenanceStatistieken,
  BrandveiligheidPage,
  KlimatisatiePage,
  PlanningPage,
  EnergieprestatiesPage,
} from "shared/components/maintenance/pages";
import CategoryAssetsPage from "screens/assets/CategoryAssetsPage";
import Units from "screens/units/units";
import { BuildingAttributeManager } from "screens/sites/components/BuildingAttributeManager";
import ClusterDetail from "screens/sites/clusterDetail";
import { MassMailPage } from "screens/mass-mail";
import Clusters from "screens/sites/clusters";
import { SegmentsList } from "screens/sites/segments/SegmentsList";
import SegmentDetail from "screens/sites/segments/SegmentDetail";
import UploadFilesPage from "screens/uploadFiles/uploadFiles";
import HomeNew from "screens/home/homeNew";
import Draaiboeken from "screens/draaiboeken/draaiboeken";
import { EllaChat } from "screens/ella/EllaChat";
import { BuildingFinancialsPage } from "screens/building-financials";
import { CompanyEquipmentPage } from "screens/company-equipment";
import RentalEmailPreview from "screens/settings/rental-email-preview";
import AssetCategoryEditorPage from "screens/settings/AssetCategoryEditorPage";
import DueDiligenceCategoryEditorPage from "screens/settings/DueDiligenceCategoryEditor";
import DueDiligenceCategoryDetailPage from "screens/buildings/pages/DueDiligenceCategoryDetailPage";
import CompanyDetail from "screens/vennootschappen/companyDetail/companyDetail";
import { MaintenanceEventDetailPage } from "./screens/maintenance/MaintenanceEventDetailPage";
import { WalkthroughProvider } from "shared/components/walkthrough";

const clerkPubKey = import.meta.env.VITE_CLERK;

/** Redirect old /assets/new/:assetId to /asset/:assetId */
const AssetCreatorRedirect = () => {
  const { assetId } = useParams<{ assetId: string }>();
  return <Navigate to={`/asset/${assetId}`} replace />;
};

/**
 * Redirects /maintenance/energieprestaties → /renovation/energieprestaties
 * Preserves query params (e.g. ?epcLabel=A) so deep-links and bookmarks survive.
 */
const EnergieprestatiesRedirect = () => {
  const location = useLocation();
  return (
    <Navigate to={`/renovation/energieprestaties${location.search}`} replace />
  );
};

/**
 * Redirects de oude renovatieticket-routes naar /renotickets:
 *  - /reno-helpdesk/tickets       → /renotickets
 *  - /reno-helpdesk/tickets/:id   → /renotickets/:id
 *  - /reno-desk/tickets/:id       → /renotickets/:id  (oude notificatielinks)
 * Querystring (bv. ?channel=supplier) blijft behouden.
 */
const RenoTicketsListRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/renotickets${location.search}`} replace />;
};

const RenoTicketsDetailRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={`/renotickets/${id}${location.search}`} replace />;
};

const ClerkProviderWithRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the upload-files route (could be picture upload)
  const isUploadFilesRoute = location.pathname === "/upload-files";

  return (
    <ClerkProvider publishableKey={clerkPubKey} navigate={(to) => navigate(to)}>
      <TooltipProvider delayDuration={100}>
        <KeyboardShortcutsProvider>
          <UsersProvider>
            <SettingsProvider>
              <PermissionsProvider>
                <ResourcesProvider>
                  <EntityFieldProvider>
                    <TasksProvider>
                      {/* Public routes: QR code reports (no auth required) */}
                      {(location.pathname.startsWith('/report/asset/') ||
                        location.pathname.startsWith('/report/maintenance/')) ? (
                        <Routes>
                          <Route
                            path="/report/asset/:assetId"
                            element={<ReportAssetIssuePage />}
                          />
                          <Route
                            path="/report/maintenance/:token"
                            element={<ReportMaintenancePage />}
                          />
                        </Routes>
                      ) : (
                      <>
                      <SignedIn>
                        <WalkthroughProvider>
                        {/* Conditionally render MenuProvider */}

                        <MenuProvider>
                          {/* Hide NavMenu on mobile when uploading files (will be replaced by custom header in UploadFilesPage) */}
                          <div
                            className={
                              isUploadFilesRoute ? "hidden md:block" : ""
                            }
                          >
                            <NavMenu />
                          </div>
                        </MenuProvider>
                        <RoutesWrapper>
                          <ScrollToTop />
                          <Routes>
                            <Route path="/" element={<HomeNew />} />
                            <Route
                              path="/draaiboeken"
                              element={<Draaiboeken />}
                            />
                            <Route
                              path="/rental/candidacies"
                              element={
                                <RequireRentalAccess showError>
                                  <Candidacies />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/candidacies/:id"
                              element={
                                <RequireRentalAccess showError>
                                  <CandidacyDetail />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/due-diligence"
                              element={
                                <RequireDueDiligenceAccess showError>
                                  <DueDiligence />
                                </RequireDueDiligenceAccess>
                              }
                            />
                            <Route
                              path="/renovation"
                              element={
                                <RequireRenovationAccess showError>
                                  <Renovation />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Maintenance Routes */}
                            <Route
                              path="/maintenance"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <Navigate
                                    to="/maintenance/statistieken"
                                    replace
                                  />
                                </RequireMaintenanceAccess>
                              }
                            />
                            <Route
                              path="/maintenance/statistieken"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <MaintenanceStatistieken />
                                </RequireMaintenanceAccess>
                              }
                            />
                            <Route
                              path="/maintenance/brandveiligheid"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <BrandveiligheidPage />
                                </RequireMaintenanceAccess>
                              }
                            />
                            {/* Dynamic category asset pages */}
                            <Route
                              path="/assets/category/:slug"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <CategoryAssetsPage />
                                </RequireMaintenanceAccess>
                              }
                            />
                            {/* Legacy redirects for old meter/toestellen/liften pages */}
                            <Route path="/maintenance/meters" element={<Navigate to="/assets/category/meters" replace />} />
                            <Route path="/maintenance/toestellen" element={<Navigate to="/assets/category/keukentoestellen" replace />} />
                            <Route path="/maintenance/liften" element={<Navigate to="/assets/category/liften" replace />} />
                            <Route
                              path="/maintenance/klimatisatie"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <KlimatisatiePage />
                                </RequireMaintenanceAccess>
                              }
                            />
                            <Route
                              path="/maintenance/planning"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <PlanningPage />
                                </RequireMaintenanceAccess>
                              }
                            />
                            {/* Energieprestaties moved to Renovatie — old URL redirects (preserving query params) */}
                            <Route
                              path="/maintenance/energieprestaties"
                              element={<EnergieprestatiesRedirect />}
                            />
                            <Route
                              path="/renovation/energieprestaties"
                              element={
                                <PermissionGuard
                                  permissions={["canAccessRenovation", "canAccessMaintenance"]}
                                  requireAll={false}
                                  showError
                                >
                                  <EnergieprestatiesPage />
                                </PermissionGuard>
                              }
                            />
                            <Route
                              path="/maintenance-event/:eventId"
                              element={
                                <RequireMaintenanceAccess showError>
                                  <MaintenanceEventDetailPage />
                                </RequireMaintenanceAccess>
                              }
                            />
                            <Route
                              path="/conversations"
                              element={
                                <RequireConversationsAccess showError>
                                  <TwilioConversations />
                                </RequireConversationsAccess>
                              }
                            />
                            <Route
                              path="/mass-mail"
                              element={
                                <RequireContactsAccess showError>
                                  <MassMailPage />
                                </RequireContactsAccess>
                              }
                            />
                            {/* Cluster Routes */}
                            <Route
                              path="/clusters"
                              element={
                                <RequireClustersAccess showError>
                                  <Clusters />
                                </RequireClustersAccess>
                              }
                            />
                            <Route
                              path="/clusters/:id"
                              element={
                                <RequireClustersAccess showError>
                                  <ClusterDetail />
                                </RequireClustersAccess>
                              }
                            />
                            {/* Segment Routes */}
                            <Route
                              path="/segments"
                              element={
                                <RequireClustersAccess showError>
                                  <SegmentsList />
                                </RequireClustersAccess>
                              }
                            />
                            <Route
                              path="/segments/:id"
                              element={
                                <RequireClustersAccess showError>
                                  <SegmentDetail />
                                </RequireClustersAccess>
                              }
                            />
                            <Route
                              path="/building-attributes"
                              element={
                                <RequireBuildingAttributesAccess showError>
                                  <BuildingAttributeManager />
                                </RequireBuildingAttributesAccess>
                              }
                            />
                            <Route
                              path="/conversations/:conversationSid"
                              element={
                                <RequireConversationsAccess showError>
                                  <TwilioConversations />
                                </RequireConversationsAccess>
                              }
                            />
                            {/* Budgets List - must be before :id route */}
                            {/*<Route path="/renovation/budgets" element={
                    <RequireRenovationAccess showError>
                      <BudgetsList />
                    </RequireRenovationAccess>
                  } />*/}
                            {/* Documents List - must be before :id route */}
                            <Route
                              path="/renovation/documents"
                              element={
                                <RequireRenovationAccess showError>
                                  <RenovationDocumentsList />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Investment Items List - must be before :id route */}
                            <Route
                              path="/renovation/investments"
                              element={
                                <RequireRenovationAccess showError>
                                  <InvestmentItemsListPage />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Investment Item Deep Link - /investment-lines/:id opens detail panel */}
                            <Route
                              path="/investment-lines/:id"
                              element={
                                <RequireRenovationAccess showError>
                                  <InvestmentItemDetailRoute />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Projects List - must be before :id route */}
                            <Route
                              path="/renovation/projects"
                              element={
                                <RequireRenovationAccess showError>
                                  <RenovationProjectsListPage />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Project Detail - uses new UniversalDetailPage structure */}
                            <Route
                              path="/renovation/:id"
                              element={
                                <RequireRenovationAccess showError>
                                  <RenovationProjectDetailPage />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Project Part Detail - dedicated page for part details */}
                            <Route
                              path="/renovation/:projectId/part/:partId"
                              element={
                                <RequireRenovationAccess showError>
                                  <ProjectPartDetailPage />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Renovatietickets */}
                            <Route
                              path="/renotickets"
                              element={
                                <RequireRenovationAccess showError>
                                  <RenoHelpdeskListPage />
                                </RequireRenovationAccess>
                              }
                            />
                            <Route
                              path="/renotickets/:id"
                              element={
                                <RequireRenovationAccess showError>
                                  <RenoHelpdeskTicketDetailPage />
                                </RequireRenovationAccess>
                              }
                            />
                            {/* Redirects van de oude reno-routes naar /renotickets */}
                            <Route path="/reno-helpdesk/tickets" element={<RenoTicketsListRedirect />} />
                            <Route path="/reno-helpdesk/tickets/:id" element={<RenoTicketsDetailRedirect />} />
                            <Route path="/reno-desk/tickets/:id" element={<RenoTicketsDetailRedirect />} />
                            <Route
                              path="/rental"
                              element={
                                <RequireRentalAccess showError>
                                  <Rental />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/statistics"
                              element={
                                <RequireRentalAccess showError>
                                  <RentalStatistics />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/milestones"
                              element={
                                <RequireRentalAccess showError>
                                  <Milestones />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/vacancies"
                              element={
                                <RequireRentalAccess showError>
                                  <Vacancies />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/incasso"
                              element={
                                <RequireRentalAccess showError>
                                  <Incasso />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/incasso/kanban"
                              element={
                                <RequireRentalAccess showError>
                                  <IncassoKanbanPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/incasso/list"
                              element={
                                <RequireRentalAccess showError>
                                  <IncassoListPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/incasso/dossier/:id"
                              element={
                                <RequireRentalAccess showError>
                                  <IncassoDossierDetailPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/watchlist"
                              element={
                                <RequireRentalAccess showError>
                                  <Watchlist />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/missing-documents"
                              element={
                                <RequireRentalAccess showError>
                                  <MissingDocuments />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/all-contracts"
                              element={
                                <RequireRentalAccess showError>
                                  <AllContractsPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/value-creation"
                              element={
                                <RequireRentalAccess showError>
                                  <ValueCreation />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/contracts-this-year"
                              element={
                                <RequireRentalAccess showError>
                                  <ContractsThisYearPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/incoming-tenants"
                              element={
                                <RequireRentalAccess showError>
                                  <IncomingTenants />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/outgoing-tenants"
                              element={
                                <RequireRentalAccess showError>
                                  <OutgoingTenants />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/herverhuur"
                              element={
                                <RequireRentalAccess showError>
                                  <HerverhuurOverzicht />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/indexaties"
                              element={
                                <RequireRentalAccess showError>
                                  <IndexatiesPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/indexaties"
                              element={
                                <RequireRentalAccess showError>
                                  <IndexatiesPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/rental/indexaties/calculate/:contractId"
                              element={
                                <RequireRentalAccess showError>
                                  <IndexationCalculationPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/indexaties/calculate/:contractId"
                              element={
                                <RequireRentalAccess showError>
                                  <IndexationCalculationPage />
                                </RequireRentalAccess>
                              }
                            />
                            <Route
                              path="/task/:taskId"
                              element={
                                <RequireTasksAccess showError>
                                  <Tasks />
                                </RequireTasksAccess>
                              }
                            />
                            <Route
                              path="/tasks/:taskId"
                              element={
                                <RequireTasksAccess showError>
                                  <Tasks />
                                </RequireTasksAccess>
                              }
                            />
                            <Route
                              path="/tasks"
                              element={
                                <RequireTasksAccess showError>
                                  <Tasks />
                                </RequireTasksAccess>
                              }
                            />
                            <Route
                              path="/documents"
                              element={
                                <RequireDocumentsAccess showError>
                                  <FilesOverview />
                                </RequireDocumentsAccess>
                              }
                            />
                            <Route
                              path="/documents/building/:buildingId"
                              element={
                                <RequireDocumentsAccess showError>
                                  <FilesOverview />
                                </RequireDocumentsAccess>
                              }
                            />
                            <Route
                              path="/documents/unit/:unitId"
                              element={
                                <RequireDocumentsAccess showError>
                                  <FilesOverview />
                                </RequireDocumentsAccess>
                              }
                            />
                            <Route
                              path="/downloads/zip/:jobId"
                              element={<ZipDownloadPage />}
                            />
                            <Route
                              path="/meldingen"
                              element={<NotificationsDashboard />}
                            />
                            <Route
                              path="/pictures"
                              element={
                                <RequirePicturesAccess showError>
                                  <ImagesGallery />
                                </RequirePicturesAccess>
                              }
                            />
                            <Route
                              path="/twilio-conversations"
                              element={
                                <RequireConversationsAccess showError>
                                  <TwilioConversations />
                                </RequireConversationsAccess>
                              }
                            />
                            <Route
                              path="/pictures/building/:buildingId"
                              element={
                                <RequirePicturesAccess showError>
                                  <ImagesGallery />
                                </RequirePicturesAccess>
                              }
                            />
                            <Route
                              path="/pictures/unit/:unitId"
                              element={
                                <RequirePicturesAccess showError>
                                  <ImagesGallery />
                                </RequirePicturesAccess>
                              }
                            />
                            <Route
                              path="/acquisitions"
                              element={
                                <RequireAcquisitionsAccess showError>
                                  <Acquisitions />
                                </RequireAcquisitionsAccess>
                              }
                            />
                            <Route
                              path="/acquisitions/statistics"
                              element={
                                <RequireAcquisitionsAccess showError>
                                  <AcquisitionStatisticsPage />
                                </RequireAcquisitionsAccess>
                              }
                            />
                            <Route
                              path="/acquisitions/:acquisitionId/detail"
                              element={
                                <RequireAcquisitionsAccess showError>
                                  <AcquisitionDetails />
                                </RequireAcquisitionsAccess>
                              }
                            />
                            <Route
                              path="/contacts"
                              element={
                                <RequireContactsAccess showError>
                                  <Contacts />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/contacts/gebouwcontacten"
                              element={
                                <RequireContactsAccess showError>
                                  <GebouwContacten />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/helpdesk"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <Navigate to="/helpdesk/list" replace />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/list"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <HelpdeskListPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/kanban"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <HelpdeskKanbanPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/statistics"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <HelpdeskStatisticsPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/opex-invoices"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <OpexInvoicesPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/new"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <QuickAddTicketPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/:id"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <HelpdeskTicketDetailPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/insurances"
                              element={
                                <RequireInsuranceAccess showError>
                                  <InsuranceFiles />
                                </RequireInsuranceAccess>
                              }
                            />
                            <Route
                              path="/insurance/detail/:id"
                              element={
                                <RequireInsuranceAccess showError>
                                  <InsuranceDetail />
                                </RequireInsuranceAccess>
                              }
                            />
                            <Route
                              path="/helpdesk/building/:buildingId"
                              element={
                                <RequireHelpdeskAccess showError>
                                  <HelpdeskListPage />
                                </RequireHelpdeskAccess>
                              }
                            />
                            <Route
                              path="/contact/new"
                              element={
                                <RequireContactsAccess showError>
                                  <ContactDetails />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/company/new"
                              element={
                                <RequireContactsAccess showError>
                                  <CompanyDetails />
                                </RequireContactsAccess>
                              }
                            />
                            {/* Contact Detail Page */}
                            <Route
                              path="/contact/:id"
                              element={
                                <RequireContactsAccess showError>
                                  <ContactDetails />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/company/:id"
                              element={
                                <RequireContactsAccess showError>
                                  <CompanyDetails />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/buildings"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Buildings />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/units"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Units />
                                </RequireBuildingsAccess>
                              }
                            />
                            {/* Building routes - base and all tabs */}
                            <Route
                              path="/building/:id"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/units"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/verhuur"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/verhuur/nieuw-contract"
                              element={
                                <RequireBuildingsAccess showError>
                                  <ContractCreatePage />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/financieel"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/due-diligence"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/due-diligence-nieuw"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            {/* Due Diligence Category Detail Pages (open in new tab) */}
                            <Route
                              path="/building/:buildingId/due-diligence/:categoryId"
                              element={
                                <RequireBuildingsAccess showError>
                                  <DueDiligenceCategoryDetailPage />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:buildingId/unit/:unitId/due-diligence/:categoryId"
                              element={
                                <RequireBuildingsAccess showError>
                                  <DueDiligenceCategoryDetailPage />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/onderhoud"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/renovatie"
                              element={
                                <RequireBuildingsAccess showError>
                                  <RequireRenovationAccess showError>
                                    <Building />
                                  </RequireRenovationAccess>
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/bouwschil"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/contacts"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/attributen"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/plattegrond"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/investeringen"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/voorzieningen"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/historiek"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/dropzone"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:id/mappenstructuur"
                              element={
                                <RequireBuildingsAccess showError>
                                  <Building />
                                </RequireBuildingsAccess>
                              }
                            />
                            {/* Legacy renovation route support - redirects to new route */}
                            <Route
                              path="/building/:id/renovation"
                              element={
                                <RequireBuildingsAccess showError>
                                  <RequireRenovationAccess showError>
                                    <Building />
                                  </RequireRenovationAccess>
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/building/:buildingId/unit/new"
                              element={
                                <RequireBuildingsAccess showError>
                                  <UnitInformation />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/unit/:unitId"
                              element={
                                <RequireBuildingsAccess showError>
                                  <UnitInformation />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/unit/detail/:unitId"
                              element={
                                <RequireBuildingsAccess showError>
                                  <UnitInformation />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/company/:id"
                              element={
                                <RequireContactsAccess showError>
                                  <CompanyDetails />
                                </RequireContactsAccess>
                              }
                            />
                            <Route
                              path="/agenda"
                              element={<SharedCalendarPage />}
                            />
                            <Route
                              path="/settings"
                              element={
                                <RequireSettingsAccess showError>
                                  <Settings />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/settings/rental-email-preview"
                              element={
                                <RequireSettingsAccess showError>
                                  <RentalEmailPreview />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/settings/voorzieningen-categorieen/nieuw"
                              element={
                                <RequireSettingsAccess showError>
                                  <AssetCategoryEditorPage />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/settings/voorzieningen-categorieen/:categoryId"
                              element={
                                <RequireSettingsAccess showError>
                                  <AssetCategoryEditorPage />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/settings/due-diligence-categorieen/nieuw"
                              element={
                                <RequireSettingsAccess showError>
                                  <DueDiligenceCategoryEditorPage />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/settings/due-diligence-categorieen/:categoryId"
                              element={
                                <RequireSettingsAccess showError>
                                  <DueDiligenceCategoryEditorPage />
                                </RequireSettingsAccess>
                              }
                            />
                            <Route
                              path="/vennootschappen"
                              element={
                                <RequireAnyPermission
                                  permissions={[
                                    "canAccessGeneral",
                                    "canAccessCorporate",
                                  ]}
                                  showError
                                >
                                  <Vennootschappen />
                                </RequireAnyPermission>
                              }
                            />
                            <Route
                              path="/vennootschappen/:id"
                              element={
                                <RequireAnyPermission
                                  permissions={[
                                    "canAccessGeneral",
                                    "canAccessCorporate",
                                  ]}
                                  showError
                                >
                                  <CompanyDetail />
                                </RequireAnyPermission>
                              }
                            />
                            <Route
                              path="/vennootschappen/merge/:jobId"
                              element={
                                <RequireAnyPermission
                                  permissions={[
                                    "canAccessGeneral",
                                    "canAccessCorporate",
                                  ]}
                                  showError
                                >
                                  <MergerProgressPage />
                                </RequireAnyPermission>
                              }
                            />
                            <Route
                              path="/financials-gebouwen"
                              element={
                                <RequireCorporateAccess showError>
                                  <BuildingFinancialsPage />
                                </RequireCorporateAccess>
                              }
                            />
                            <Route
                              path="/company-equipment"
                              element={
                                <RequireCorporateAccess showError>
                                  <CompanyEquipmentPage />
                                </RequireCorporateAccess>
                              }
                            />
                            <Route
                              path="/notes"
                              element={
                                <RequireNotesAccess showError>
                                  <NotesOverviewSimple />
                                </RequireNotesAccess>
                              }
                            />
                            <Route
                              path="/bug-reports"
                              element={
                                <RequireGeneralAccess showError>
                                  <BugReportsPage />
                                </RequireGeneralAccess>
                              }
                            />
                            <Route
                              path="/bug-reports/epics"
                              element={
                                <RequireGeneralAccess showError>
                                  <BugReportsPage />
                                </RequireGeneralAccess>
                              }
                            />
                            <Route
                              path="/bug-reports/epics/:id"
                              element={
                                <RequireGeneralAccess showError>
                                  <EpicDetailPage />
                                </RequireGeneralAccess>
                              }
                            />
                            <Route
                              path="/bug-reports/:id"
                              element={
                                <RequireGeneralAccess showError>
                                  <BugReportDetailPage />
                                </RequireGeneralAccess>
                              }
                            />
                            <Route
                              path="/pricing-simulator"
                              element={
                                <RequireGeneralAccess showError>
                                  <PricingSimulatorPage />
                                </RequireGeneralAccess>
                              }
                            />
                            <Route
                              path="/upload-files"
                              element={<UploadFilesPage />}
                            />
                            <Route path="/ella" element={<EllaChat />} />
                  <Route path="/entity-schema/:entityName" element={
                    <RequireSettingsAccess showError>
                      <EntitySchemaVisualization />
                    </RequireSettingsAccess>
                  } />
                  <Route path="/document-generator" element={
                    <RequireDocumentsAccess showError>
                      <DocumentGenerator />
                    </RequireDocumentsAccess>
                  } />
                  <Route path="/documents" element={
                    <RequireDocumentsAccess showError>
                      <FilesOverview />
                    </RequireDocumentsAccess>
                  } />

                            {/* Access Control Routes */}
                            <Route
                              path="/accesscontrol"
                              element={
                                <RequireBuildingsAccess showError>
                                  <AccessControlPage />
                                </RequireBuildingsAccess>
                              }
                            />

                            {/* Asset Management Routes */}
                            <Route
                              path="/assets"
                              element={
                                <RequireBuildingsAccess showError>
                                  <AssetsScreen />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/assets/bulk-create"
                              element={
                                <RequireBuildingsAccess showError>
                                  <AssetBulkCreatorPage />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/assets/new/:assetId"
                              element={<AssetCreatorRedirect />}
                            />
                            <Route
                              path="/category-management"
                              element={
                                <RequireBuildingsAccess showError>
                                  <AssetCategoryManagementScreen />
                                </RequireBuildingsAccess>
                              }
                            />
                            <Route
                              path="/asset/:assetId"
                              element={
                                <RequireBuildingsAccess showError>
                                  <AssetDetailPage />
                                </RequireBuildingsAccess>
                              }
                            />
                          </Routes>
                          {/* <Footer /> */}
                          <ChatPopup /> {/* Add ChatPopup here */}
                        </RoutesWrapper>
                        </WalkthroughProvider>
                      </SignedIn>
                      <SignedOut>
                        <RedirectToSignIn />
                      </SignedOut>
                      </>
                      )}
                    </TasksProvider>
                  </EntityFieldProvider>
                </ResourcesProvider>
              </PermissionsProvider>
            </SettingsProvider>
          </UsersProvider>
        </KeyboardShortcutsProvider>
      </TooltipProvider>
    </ClerkProvider>
  );
};

export default ClerkProviderWithRoutes;
